import { Box, Chip, SxProps } from '@hermes/web-components'
import { useContext, useMemo } from 'react'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import highlight from '../../../../utils/highlightText'
import {
  RootContainer,
  ItemRootContainer,
  ItemTitleContainer,
  ItemTitle,
  ItemSubTitle,
  SearchModalHeaderTextContainer,
  SearchModalHeaderText,
  SearchModalKeyContainer,
  ImageContainer
} from './styles'
import NoResultsComponent from './NoResultsComponent'
import NextImage from 'next/image'
import { HomePageContext } from '../../../../providers/HomePage'

export interface Item {
  id: number
  name: string
  logo: string
  subtitle?: string
  onSelect: () => void
  slug: string
}

interface ResultsListItemProps extends Item {
  specialistOrPractice: boolean
  searchString: string
  slug: string
}

interface ResultsListProps {
  rootContainerStyles?: SxProps
  items?: Item[]
  specialties?: Item[]
  conditionsProcedures?: Item[]
  specialistOrPractice?: boolean
  searchString: string
  handleCloseSearch?: () => void
  bubbleView?: boolean
  isMobileView?: boolean
  shouldShowProfiles?: boolean
}

type LetterMapperProps = { groups: Record<string, Array<Item>>; item: Item }

type SortByAlphabetProps = { contentToSort: Item[] }

const letterMapper = ({ groups, item }: LetterMapperProps) => {
  const firstLetter = item.name.charAt(0).toUpperCase()

  if (!groups[firstLetter]) {
    groups[firstLetter] = []
  }

  groups[firstLetter].push(item)

  return groups
}

const sortByAlphabet = ({ contentToSort }: SortByAlphabetProps) => {
  return contentToSort.sort((a, b) => {
    if (a.name === b.name) {
      return 0
    }
    return a.name < b.name ? -1 : 1
  })
}

const ResultsListItemOld = ({
  name,
  logo,
  subtitle,
  specialistOrPractice = false,
  onSelect,
  searchString
}: ResultsListItemProps) => (
  <ItemRootContainer onClick={onSelect}>
    {specialistOrPractice && logo && (
      <ImageContainer>
        <NextImage
          src={logo}
          alt="search-profile-icon"
          width={48}
          height={48}
          style={{ borderRadius: '50%' }}
          objectFit="cover"
        />
      </ImageContainer>
    )}
    <ItemTitleContainer>
      <ItemTitle
        specialistOrPractice={specialistOrPractice && !searchString}
        dangerouslySetInnerHTML={{ __html: highlight(name, searchString) }}
      />
      {specialistOrPractice && subtitle ? <ItemSubTitle>{subtitle}</ItemSubTitle> : null}
    </ItemTitleContainer>
  </ItemRootContainer>
)

const ResultsListItem = ({ name, onSelect }: ResultsListItemProps) => {
  const handleClick = () => {
    onSelect()
  }

  return (
    <div>
      <Chip
        label={name}
        onClick={handleClick}
        sx={{
          fontWeight: 500,
          fontSize: '15px',
          lineHeight: '22px',
          cursor: 'pointer',
          '&:hover': {
            background: '#CCFAF6'
          },
          backgroundColor: '#EAEEFC',
          color: '#1A3586'
        }}
      />
    </div>
  )
}

type AutocompleteKeywordsContainerProps = {
  groupedContentList: Record<string, Array<Item>>
  specialistOrPractice: boolean
  searchString: string
}

const AutocompleteKeywordsContainer = ({
  groupedContentList,
  specialistOrPractice,
  searchString
}: AutocompleteKeywordsContainerProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', overflowX: 'hidden' }}>
    {Object.keys(groupedContentList)?.map((key, index) => (
      <Box key={`${key}${index}`} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <SearchModalKeyContainer>{key}</SearchModalKeyContainer>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {groupedContentList[key]?.map((item) => (
            <ResultsListItem
              key={`search-${item.subtitle}-${item.id}`}
              specialistOrPractice={specialistOrPractice}
              searchString={searchString}
              {...item}
            />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
)

const ResultsList = ({
  rootContainerStyles = {},
  items = [],
  specialties = [],
  conditionsProcedures = [],
  specialistOrPractice = false,
  searchString,
  bubbleView = false,
  handleCloseSearch = () => {},
  isMobileView = false,
  shouldShowProfiles = false
}: ResultsListProps) => {
  const translate = useTranslateMessage()
  const { isSpecialistSearch } = useContext(HomePageContext)

  const reorderedConditionsProcedures = useMemo(() => {
    if (bubbleView) {
      return sortByAlphabet({ contentToSort: conditionsProcedures })
    }
    return conditionsProcedures
  }, [conditionsProcedures, bubbleView])

  const reorderedSpecialties = useMemo(() => {
    if (bubbleView) {
      return sortByAlphabet({ contentToSort: specialties })
    }
    return specialties
  }, [specialties, bubbleView])

  const groupedConditionsProceduresByFirstLetter = useMemo(
    () =>
      reorderedConditionsProcedures.reduce((groups: Record<string, Array<Item>>, item) => {
        return letterMapper({ groups, item })
      }, {}),
    [reorderedConditionsProcedures]
  )

  const groupedSpecialtiesByFirstLetter = useMemo(
    () =>
      reorderedSpecialties.reduce((groups: Record<string, Array<Item>>, item) => {
        return letterMapper({ groups, item })
      }, {}),
    [reorderedSpecialties]
  )

  const translatedName = isSpecialistSearch ? translate('specialists') : translate('practices')

  if (items.length || shouldShowProfiles) {
    if (!items.length && shouldShowProfiles && !!searchString.length) {
      return (
        <>
          <SearchModalHeaderTextContainer
            sx={{
              marginBottom: '20px'
            }}
          >
            <SearchModalHeaderText variant="body2">{translatedName}</SearchModalHeaderText>
          </SearchModalHeaderTextContainer>
          <NoResultsComponent onClick={handleCloseSearch} withImage />
        </>
      )
    }

    return (
      <>
        <SearchModalHeaderTextContainer>
          <SearchModalHeaderText variant="body2">{translatedName}</SearchModalHeaderText>
        </SearchModalHeaderTextContainer>
        <RootContainer sx={rootContainerStyles} specialistOrPractice={specialistOrPractice}>
          {items.map((item: Item) => (
            <ResultsListItemOld
              key={`search-${item.subtitle}-${item.id}`}
              specialistOrPractice={specialistOrPractice}
              searchString={searchString}
              {...item}
            />
          ))}
        </RootContainer>
      </>
    )
  }

  return (
    <>
      <SearchModalHeaderTextContainer>
        <SearchModalHeaderText variant="body2">
          {translate(reorderedConditionsProcedures.length ? 'popular_conditions_procedures' : 'popular_specialties')}
        </SearchModalHeaderText>
      </SearchModalHeaderTextContainer>
      <RootContainer sx={rootContainerStyles} specialistOrPractice={specialistOrPractice}>
        <AutocompleteKeywordsContainer
          searchString={searchString}
          specialistOrPractice={specialistOrPractice}
          groupedContentList={groupedConditionsProceduresByFirstLetter}
        />
        {isMobileView && Object.keys(groupedSpecialtiesByFirstLetter).length !== 0 && (
          <SearchModalHeaderTextContainer>
            <SearchModalHeaderText sx={{ padding: '16px 0px !important' }} variant="body2">
              {translate('popular_specialties')}
            </SearchModalHeaderText>
          </SearchModalHeaderTextContainer>
        )}
        <AutocompleteKeywordsContainer
          searchString={searchString}
          specialistOrPractice={specialistOrPractice}
          groupedContentList={groupedSpecialtiesByFirstLetter}
        />
      </RootContainer>
    </>
  )
}

export default ResultsList
